// PUBLIC benchmark results page.
//
// Renders ONLY what /public/benchmark returns: scenario labels, scores,
// robustness (PDR), and prompt sensitivity. The server projection is the real
// boundary — this component must never be pointed at an admin endpoint, and no
// cost, prompt, generated code, or per-check failure detail belongs here.
const React = require('react');
const { createElement: h, useState, useEffect } = React;
const benchmarkStore = require('../lib/benchmarkStore');
require('./PublicBenchmarkPage.css');

// Score bands double as the colour scale, so a number and its colour can never
// disagree.
function band(score) {
  if (score >= 85) return 'strong';
  if (score >= 70) return 'good';
  if (score >= 50) return 'fair';
  return 'weak';
}

function ScoreBar(props) {
  var score = Math.max(0, Math.min(100, Number(props.score) || 0));
  return h('div', { className: 'bm-bar' },
    h('div', { className: 'bm-bar-fill bm-bar-fill--' + band(score), style: { width: score + '%' } })
  );
}

// Vertical bar chart comparing workers at a glance. Bars are sized by score and
// share the same band colours as everything else on the page, so a bar's colour
// and its number can never disagree.
function BarChart(props) {
  var rows = (props.rows || []).filter(function (r) { return typeof r.score === 'number'; });
  if (rows.length === 0) return null;

  return h('div', { className: 'bm-chart' },
    h('div', { className: 'bm-chart-plot' },
      // Reference lines at 25/50/75/100 give the bars a scale to be read against.
      [100, 75, 50, 25].map(function (tick) {
        return h('div', { className: 'bm-chart-grid', style: { bottom: tick + '%' }, key: tick },
          h('span', { className: 'bm-chart-tick' }, tick)
        );
      }),
      h('div', { className: 'bm-chart-bars' },
        rows.map(function (r) {
          return h('div', { className: 'bm-chart-col', key: r.key || r.label },
            h('div', { className: 'bm-chart-value' }, r.score),
            h('div', {
              className: 'bm-chart-bar bm-chart-bar--' + band(r.score),
              // Minimum height keeps a 0 score visible as a bar rather than nothing.
              style: { height: 'max(3px, ' + r.score + '%)' },
              title: r.label + ': ' + r.score
            })
          );
        })
      )
    ),
    h('div', { className: 'bm-chart-labels' },
      rows.map(function (r) {
        return h('div', { className: 'bm-chart-label', key: r.key || r.label }, r.label);
      })
    )
  );
}

// Overall score across runs, oldest → newest. Single series, so no legend —
// the section title names it. The fixed 0–100 scale matches the bar chart so
// the two charts read against the same yardstick. Hidden until there are at
// least two runs, because one point is not a trend.
function HistoryChart(props) {
  var [hover, setHover] = useState(null);
  var points = (props.history || []).filter(function (p) {
    return typeof p.overallScore === 'number' && p.finishedAt;
  });
  if (points.length < 2) return null;

  var W = 900, padL = 30, padR = 14, padT = 16, plotH = 170, H = padT + plotH + 28;
  var innerW = W - padL - padR;
  function x(i) { return padL + (i / (points.length - 1)) * innerW; }
  function y(score) { return padT + (1 - Math.max(0, Math.min(100, score)) / 100) * plotH; }
  function fmtDate(d) { return new Date(d).toLocaleDateString(); }

  var path = points.map(function (p, i) {
    return (i === 0 ? 'M' : 'L') + x(i).toFixed(1) + ' ' + y(p.overallScore).toFixed(1);
  }).join(' ');

  // Date labels under the first, middle (when there is room) and last run.
  var labelIdx = points.length >= 5
    ? [0, Math.floor((points.length - 1) / 2), points.length - 1]
    : [0, points.length - 1];

  var tip = null;
  if (hover != null) {
    var hp = points[hover];
    // Clamp horizontally so edge tooltips stay inside the plot; flip below the
    // point when it sits too close to the top.
    var tx = Math.max(padL + 60, Math.min(W - padR - 60, x(hover)));
    var above = y(hp.overallScore) > 40;
    var ty = above ? y(hp.overallScore) - 14 : y(hp.overallScore) + 24;
    tip = h('text', { className: 'bm-trend-tip', x: tx, y: ty, textAnchor: 'middle' },
      hp.overallScore + ' · ' + fmtDate(hp.finishedAt));
  }

  return h('div', { className: 'bm-section' },
    h('h2', { className: 'bm-h2' }, 'Progress over time'),
    h('p', { className: 'bm-note' }, 'Overall score for every published benchmark run, oldest to newest.'),
    h('svg', {
      className: 'bm-trend', viewBox: '0 0 ' + W + ' ' + H,
      role: 'img', 'aria-label': 'Overall benchmark score across runs'
    },
      [100, 75, 50, 25, 0].map(function (tick) {
        return h('g', { key: tick },
          h('line', {
            className: tick === 0 ? 'bm-trend-grid bm-trend-grid--base' : 'bm-trend-grid',
            x1: padL, x2: W - padR, y1: y(tick), y2: y(tick)
          }),
          h('text', { className: 'bm-trend-tick', x: padL - 6, y: y(tick) + 3, textAnchor: 'end' }, tick)
        );
      }),
      h('path', { className: 'bm-trend-line', d: path }),
      points.map(function (p, i) {
        return h('g', { key: i },
          h('circle', { className: 'bm-trend-dot', cx: x(i), cy: y(p.overallScore), r: hover === i ? 5 : 3.5 }),
          // Oversized invisible hit target so hovering doesn't need pixel aim.
          h('circle', {
            className: 'bm-trend-hit', cx: x(i), cy: y(p.overallScore), r: 14,
            onMouseEnter: function () { setHover(i); },
            onMouseLeave: function () { setHover(null); }
          })
        );
      }),
      tip,
      labelIdx.map(function (i) {
        return h('text', {
          className: 'bm-trend-date', key: i, x: x(i), y: padT + plotH + 18,
          textAnchor: i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'
        }, fmtDate(points[i].finishedAt));
      })
    )
  );
}

function WorkerCard(props) {
  var w = props.worker;
  return h('div', { className: 'bm-worker' },
    h('div', { className: 'bm-worker-head' },
      h('span', { className: 'bm-worker-label' }, w.label),
      h('span', { className: 'bm-worker-score bm-worker-score--' + band(w.score) }, w.score)
    ),
    h(ScoreBar, { score: w.score })
  );
}

function ScenarioTable(props) {
  var byWorker = {};
  (props.cases || []).forEach(function (c) {
    if (!byWorker[c.workerId]) byWorker[c.workerId] = [];
    byWorker[c.workerId].push(c);
  });
  var labelFor = {};
  (props.workers || []).forEach(function (w) { labelFor[w.workerId] = w.label; });

  return h('div', { className: 'bm-scenarios' },
    Object.keys(byWorker).map(function (workerId) {
      return h('div', { className: 'bm-scenario-group', key: workerId },
        h('div', { className: 'bm-scenario-group-title' }, labelFor[workerId] || workerId),
        h('div', { className: 'bm-scenario-rows' },
          byWorker[workerId].map(function (c) {
            return h('div', { className: 'bm-scenario-row', key: c.scenarioId },
              h('span', { className: 'bm-scenario-name' }, c.scenarioLabel),
              h('span', { className: 'bm-scenario-bar' }, h(ScoreBar, { score: c.composite })),
              h('span', { className: 'bm-scenario-score bm-scenario-score--' + band(c.composite) }, c.composite)
            );
          })
        )
      );
    })
  );
}

// Robustness reads INVERTED — a high PDR is bad — so it gets its own colour
// logic rather than reusing band().
function RobustnessTable(props) {
  if (!props.rows || !props.rows.length) return null;
  return h('div', { className: 'bm-section' },
    h('h2', { className: 'bm-h2' }, 'Robustness under adversarial input'),
    h('p', { className: 'bm-note' },
      'Each attack rewrites the user\'s request while preserving its meaning. ',
      h('strong', null, 'PDR'), ' (performance drop rate) is how much the score fell versus the same ',
      'scenario run clean. Lower is better; 0% means the attack had no effect.'
    ),
    h('div', { className: 'bm-rob-rows' },
      props.rows.map(function (r) {
        var severity = r.pdr >= 30 ? 'weak' : r.pdr >= 15 ? 'fair' : r.pdr >= 5 ? 'good' : 'strong';
        return h('div', { className: 'bm-rob-row', key: r.attack },
          h('span', { className: 'bm-rob-level' }, r.level),
          h('span', { className: 'bm-rob-label' }, r.label),
          h('span', { className: 'bm-rob-score' }, r.score),
          h('span', { className: 'bm-rob-pdr bm-rob-pdr--' + severity }, r.pdr + '%')
        );
      })
    )
  );
}

function PromptTable(props) {
  if (!props.rows || props.rows.length < 2) return null;
  var scores = props.rows.map(function (p) { return p.score; });
  var spread = Math.max.apply(null, scores) - Math.min.apply(null, scores);
  return h('div', { className: 'bm-section' },
    h('h2', { className: 'bm-h2' }, 'Sensitivity to phrasing'),
    h('p', { className: 'bm-note' },
      'The same intent, written five ways. A large spread means the result depends on how you ask — ',
      'the spread here is ', h('strong', null, spread + ' points'), '.'
    ),
    h('div', { className: 'bm-rob-rows' },
      props.rows.map(function (p) {
        return h('div', { className: 'bm-rob-row', key: p.variant },
          h('span', { className: 'bm-rob-label bm-rob-label--wide' }, p.label),
          h('span', { className: 'bm-scenario-bar' }, h(ScoreBar, { score: p.score })),
          h('span', { className: 'bm-rob-score bm-rob-score--' + band(p.score) }, p.score)
        );
      })
    )
  );
}

function PublicBenchmarkPage(props) {
  var [run, setRun] = useState(null);
  var [history, setHistory] = useState([]);
  var [state, setState] = useState('loading');

  useEffect(function () {
    var cancelled = false;
    benchmarkStore.getPublicBenchmark()
      .then(function (r) {
        if (cancelled) return;
        setRun(r.run);
        setHistory(r.history);
        setState(r.run ? 'ready' : 'empty');
      })
      .catch(function () { if (!cancelled) setState('error'); });
    return function () { cancelled = true; };
  }, []);

  if (state === 'loading') return h('div', { className: 'bm-page bm-page--msg' }, 'Loading benchmark…');
  if (state === 'error') return h('div', { className: 'bm-page bm-page--msg' }, 'Could not load the benchmark results.');
  if (state === 'empty') return h('div', { className: 'bm-page bm-page--msg' }, 'No benchmark has been published yet.');

  return h('div', { className: 'bm-page' },
    h('div', { className: 'bm-hero' },
      h('h1', { className: 'bm-h1' }, 'Alleflow AI benchmark'),
      h('p', { className: 'bm-sub' },
        'How our AI workers score on a fixed suite of automation tasks. Every case is graded by ',
        'deterministic checks plus an independent LLM judge scoring a published rubric.'
      ),
      h('div', { className: 'bm-overall' },
        h('div', { className: 'bm-overall-score bm-overall-score--' + band(run.overallScore) }, run.overallScore),
        h('div', { className: 'bm-overall-caption' },
          h('div', null, 'Overall score'),
          run.finishedAt ? h('div', { className: 'bm-overall-date' },
            'Last run ' + new Date(run.finishedAt).toLocaleDateString()) : null
        )
      )
    ),

    h(HistoryChart, { history: history }),

    h('div', { className: 'bm-section' },
      h('h2', { className: 'bm-h2' }, 'By worker'),
      h(BarChart, {
        rows: (run.workers || []).map(function (w) {
          return { key: w.workerId, label: w.label, score: w.score };
        })
      }),
      h('div', { className: 'bm-workers' },
        (run.workers || []).map(function (w) { return h(WorkerCard, { worker: w, key: w.workerId }); })
      )
    ),

    h('div', { className: 'bm-section' },
      h('h2', { className: 'bm-h2' }, 'By scenario'),
      h(ScenarioTable, { cases: run.cases, workers: run.workers })
    ),

    h(RobustnessTable, { rows: run.robustness }),
    h(PromptTable, { rows: run.promptSensitivity }),

    h('div', { className: 'bm-method' },
      h('h2', { className: 'bm-h2' }, 'How this is scored'),
      h('ul', { className: 'bm-method-list' },
        h('li', null, h('strong', null, 'Deterministic checks'), ' — parseable facts: does the graph validate, does the generated code run in a sandbox, were the right diagnostic tools called, are there any literal credentials in the output. These are pass/fail and carry fixed weights.'),
        h('li', null, h('strong', null, 'LLM judge'), ' — scores 0-5 per rubric criterion on things a parser cannot check, such as whether the result actually solves the stated goal. The judge never sees the deterministic verdict.'),
        h('li', null, h('strong', null, 'Composite'), ' — the weighted total of both, as a percentage of the maximum. Nothing is dropped from the denominator: a criterion that errors scores zero rather than disappearing.'),
        h('li', null, h('strong', null, 'Headline scores are clean-input only'), ' — adversarial cases are reported separately as robustness and never blended into the quality number.')
      )
    )
  );
}

module.exports = PublicBenchmarkPage;
